import { paymentApi } from '../../services/paymentApi';
import isEqual from 'lodash/isEqual';


export function surveyDiffers(survey1, survey2) {
  // Compare editedSurvey with userData.surveyQuestions
  if (survey1.length !== survey2.length) {
    return true;
  }
  for (let i = 0; i < survey1.length; i++) {
    if (!isEqual(survey1[i], survey2[i])) {
      return true;
    }
  }
  return false;
};

// Validate a single field change for a question
export function validateQuestionFieldChange(question, field, value) {
  const newValue = parseInt(value, 10);

  if (field === 'selectMax') {
    if (newValue < question.selectMin) {
      return {
        valid: false,
        message: `Max cannot be less than Min. Adjusting max to ${question.selectMin}.`,
        adjustedValue: question.selectMin
      };
    }
    return { valid: true, adjustedValue: newValue };
  }

  if (field === 'selectMin') {
    const numOptions = question.responseOptions ? question.responseOptions.length : 0;
    if (newValue > question.selectMax) {
      return {
        valid: false,
        message: `Min cannot exceed Max. Adjusting min to ${question.selectMax}.`,
        adjustedValue: question.selectMax
      };
    } else if (newValue > numOptions && question.questionType === 'multiple-choice') {
      return {
        valid: false,
        message: `Min cannot exceed the number of options (${numOptions}). Adjusting min to ${numOptions}.`,
        adjustedValue: numOptions
      };
    }
    return { valid: true, adjustedValue: newValue };
  }

  if (field === 'maxLength') {
    if (newValue < 10) {
      return {
        valid: false,
        message: `Max Length cannot be less than 10. Adjusting to 10.`,
        adjustedValue: 10
      };
    }
    return { valid: true, adjustedValue: newValue };
  }

  if (field === 'questionText' && (!value || !value.trim())) {
    return {
      valid: false,
      message: `Question has no text. Please enter some text for this question.`,
      adjustedValue: value
    };
  }

  if (field === 'responseOptions' && question.responseOptions.some(opt => !opt.trim())) {
    return {
      valid: false,
      message: `Some options are empty. Please fill in all options.`,
      adjustedValue: value
    };
  }

  return { valid: true, adjustedValue: value };
}


// Validate a single question
export function validateQuestion(question, index) {
  console.log("IN VALIDATE QUESTION, question is", question);
  if (!question.questionText || !question.questionText.trim()) {
    return {
      valid: false,
      message: `Question #${index + 1} has no text. Please enter some text for this question.`
    };
  }

  if (question.questionType === 'open-ended') {
    if (!question.maxLength) {
      return {
        valid: false,
        message: `Question #${index + 1} must have a maxLength.`
      };
    }
  }

  if (question.questionType === 'multiple-choice') {
    if (!question.responseOptions || question.responseOptions.some(opt => !opt.trim())) {
      return {
        valid: false,
        message: `Some options for question #${index + 1} are empty. Please fill in all options.`
      };
    }
    if (question.responseOptions.length < 2) {
      return {
        valid: false,
        message: `Question #${index + 1} must have at least 2 options.`
      };
    }
    // Check selectMin and selectMax are valid integers and > 0
    const selectMin = parseInt(question.selectMin, 10);
    const selectMax = parseInt(question.selectMax, 10);
    if (
      isNaN(selectMin) || isNaN(selectMax)
    ) {
      return {
        valid: false,
        message: `Question #${index + 1} must have valid numbers for selectMin and selectMax.`
      };
    }
    if (
      selectMin < 0 || selectMin > question.responseOptions.length
    ) {
      return {
        valid: false,
        message: `Question #${index + 1} must have selectMin at least 0 and at most ${question.responseOptions.length}.`
      };
    }
    if (
      selectMax < selectMin || selectMax > question.responseOptions.length
    ) {
      return {
        valid: false,
        message: `Question #${index + 1} must have selectMax at least ${selectMin} and at most ${question.responseOptions.length}.`
      };
    }
  }

  if (question.questionType === 'open-list') {
    const selectMin = parseInt(question.selectMin, 10);
    const selectMax = parseInt(question.selectMax, 10);
    if (
      isNaN(selectMin) || isNaN(selectMax)
    ) {
      return {
        valid: false,
        message: `Question #${index + 1} (open-list) must have valid numbers for selectMin and selectMax.`
      };
    }
  }
  return { valid: true };
}

// Validate the entire survey
export function validateSurvey(questions, { isAdmin, hasActiveSubscription, FREE_RESEARCH_LIMITS }) {
  const maxQuestions = isAdmin 
    ? 20 // Admin limit
    : hasActiveSubscription 
      ? 15 // SUBSCRIPTION_LIMITS.MAX_QUESTIONS
      : 10; // FREE_RESEARCH_LIMITS.MAX_QUESTIONS

  if (questions.length > maxQuestions) {
    const questionsToRemove = questions.length - maxQuestions;
    return {
      valid: false,
      message:
        `You've exceeded the maximum questions limit. ` +
        `Please remove ${questionsToRemove} question${questionsToRemove > 1 ? 's' : ''} ` +
        `to continue${!hasActiveSubscription ? ' or upgrade to a subscription' : ''}.`
    };
  }

  for (let i = 0; i < questions.length; i++) {
    const result = validateQuestion(questions[i], i);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
} 

export async function enoughCredits(progress, isAdmin, hasActiveSubscription, walletBalance) {
  if (progress > 1 || isAdmin) {
    return true;
  }
  const userDetails = await paymentApi.getUserDetails();
  const userCredits = parseInt(userDetails.credits || '0');

  return hasActiveSubscription || userCredits > 0 || walletBalance > 0;

}

export function deepCloneSurvey(survey) {
  return survey.map(q => ({
  ...q,
  responseOptions: q.responseOptions ? [...q.responseOptions] : [],
  responsePercentages: q.responsePercentages ? [...q.responsePercentages] : [],
  }));
}